import React, { useState } from "react";
import { Icon, Button } from "antd";
import { printLabels } from "../../../utils/labelUtils";
import { PrintButtons } from "./ModalContent/PrintButtons";
import { WarningDownload } from "./ModalContent/WarningDownload";
import { ErrorList } from "./ModalContent/ErrorList";
import { SuccessMassiveLines } from "./ModalContent/SuccessMassiveLines";

const getPrintableCodes = (data, isMassive) => {
  if (isMassive) {
    return data
      ?.filter(
        (item) =>
          Array.isArray(item.packageCodes) && item.packageCodes.length > 0
      )
      .flatMap((item) => item.packageCodes);
  }
  return Array.isArray(data) ? data : [];
};

const isPreRegisterSuccess = (data, errors) =>
  data?.length > 0 && errors?.length === 0;

const isFullError = (data, errors) => data?.length === 0 && errors?.length > 0;

const isPartialSuccess = (data, errors) =>
  data?.length > 0 && errors?.length > 0;

const isInvalidFile = (data, errors) =>
  (data?.length ?? 0) === 0 && (errors?.length ?? 0) === 0; 

const renderWarningDownload = (serviceType, hasPrintableLabels) => {
  const lowerServiceType = (serviceType || "").toLowerCase();
  return (
    hasPrintableLabels &&
    (lowerServiceType.includes("activo") ||
      lowerServiceType.includes("proximidad"))
  );
};

export default function PreRegistrationModal({
  preRegisterData,
  preRegisterErrors,
  labelsData,
  labelsErrors,
  preRegisterisLoading,
  labelsIsLoading,
  actions,
  serviceType,
  isMassive,
}) {
  const {
    getLabels,
    handleResetForm,
    handleModalToggle,
    handleModalToggleWithRefresh,
  } = actions;

  const [printType, setPrintType] = useState(undefined);
  const printableCodes = getPrintableCodes(preRegisterData, isMassive);
  const hasPrintableLabels = printableCodes.length > 0;

  const handlePrintLabels = async () => {
    const refinedLabels = printableCodes.join(",");
    if (!refinedLabels || !printType) return;

    const printedLabels = await getLabels(refinedLabels, printType);
    if (printedLabels) printLabels(printedLabels);
  };

  if (preRegisterisLoading) {
    return (
      <div className="PreRegistrationModal">
        <div className="loading-message-container">
          <span>
            <b>Generando...</b>
          </span>
        </div>
        <div className="spinner-container">
          <Icon type="sync" spin />
        </div>
      </div>
    );
  }

  if (labelsIsLoading) {
    return (
      <div className="PreRegistrationModal">
        <div className="loading-message-container">
          <span>
            <b>Descargando etiquetas...</b>
          </span>
        </div>
        <div className="spinner-container">
          <Icon type="sync" />
        </div>
      </div>
    );
  }

  if (!labelsIsLoading && labelsData?.length > 0) {
    return (
      <div className="PreRegistrationModal">
        <div className="preregistration-success-result">
          <Icon type="check-circle" />
          <span>
            <b>Generación de etiqueta exitosa.</b>
          </span>
        </div>
        <div className="accept-container">
          <Button
            onClick={handleModalToggleWithRefresh}
            className="accept-button"
          >
            Aceptar
          </Button>
        </div>
      </div>
    );
  }

  if (isPreRegisterSuccess(preRegisterData, preRegisterErrors)) {
    return (
      <div className="PreRegistrationModal">
        {isMassive ? (
          <SuccessMassiveLines data={preRegisterData} />
        ) : (
          <div className="preregistration-success-result">
            <Icon type="check-circle" />
            <span>
              <b>Su envío ha sido procesado con éxito.</b>
            </span>
          </div>
        )}

        <PrintButtons printType={printType} setPrintType={setPrintType} />

        {labelsErrors.length > 0 && (
          <div className="error-container">
            <small>{labelsErrors}</small>
          </div>
        )}

        <div className="accept-container">
          <Button onClick={handlePrintLabels} className="accept-button">
            Aceptar
          </Button>
          <Button
            type="danger"
            ghost
            onClick={handleModalToggleWithRefresh}
            className="cancel-button"
          >
            Cerrar
          </Button>
        </div>

        {renderWarningDownload(serviceType, hasPrintableLabels) && (
          <WarningDownload />
        )}
      </div>
    );
  }

  if (isFullError(preRegisterData, preRegisterErrors)) {
    return (
      <div className="PreRegistrationModal">
        <div className="preregistration-error-result">
          <Icon type="close-circle" />
          <span>
            <b>
              No se ha podido pre-registrar su envío por los siguientes errores:
            </b>
          </span>
        </div>
        <ErrorList errors={preRegisterErrors} />
        <div className="accept-container">
          <Button onClick={handleModalToggle} className="accept-button">
            Aceptar
          </Button>
        </div>
      </div>
    );
  }

  if (isInvalidFile(preRegisterData, preRegisterErrors)) {
    return (
      <div className="PreRegistrationModal">
        <div className="preregistration-error-result">
          <Icon type="close-circle" />
          <span>
            <b>
              El archivo no es válido o no contiene líneas procesables. Revise el
              formato y vuelva a intentarlo.
            </b>
          </span>
        </div>

        <div className="accept-container">
          <Button onClick={handleModalToggle} className="accept-button">
            Aceptar
          </Button>
        </div>
      </div>
    );
  }


  if (isPartialSuccess(preRegisterData, preRegisterErrors)) {
    return (
      <div className="PreRegistrationModal">
        <div className="preregistration-mixed-result">
          <Icon type="exclamation-circle" />
          <span>
            <b>Su envío ha sido procesado con errores:</b>
          </span>
        </div>

        <ErrorList errors={preRegisterErrors} />

        {isMassive && <SuccessMassiveLines data={preRegisterData} />}

        {hasPrintableLabels && (
          <PrintButtons printType={printType} setPrintType={setPrintType} />
        )}

        {labelsErrors.length > 0 && (
          <div className="error-container">
            <small>{labelsErrors}</small>
          </div>
        )}

        {hasPrintableLabels ? (
          <div className="accept-container">
            <Button onClick={handlePrintLabels} className="accept-button">
              Aceptar
            </Button>
            <Button
              onClick={handleModalToggleWithRefresh}
              className="cancel-button"
            >
              Cerrar
            </Button>
          </div>
        ) : (
          <div className="accept-container">
            <Button onClick={handleModalToggle} className="accept-button">
              Aceptar
            </Button>
          </div>
        )}

        {renderWarningDownload(serviceType, hasPrintableLabels) && (
          <WarningDownload />
        )}
      </div>
    );
  }

  return <div className="PreRegistrationModal" />;
}
